import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { type Inquiry } from '~/features/inquiry/types/inquiry'
import { getColorByStatusCode } from '~/features/inquiry/utils/inquiry'
import Round from '~/shared/components/round/Round'
import theme from '~/shared/styles/theme'
import { fDate } from '~/shared/utils/format-date'

type Props = {
  data: Inquiry
}

function InquiryListItem({ data }: Props) {
  const navigate = useNavigate()
  const { id, category_desc, title, updated_at, status, status_desc } = data

  const color = getColorByStatusCode(status)

  const handleClick = (inquiryId: number) => {
    navigate(`/customer-service/inquiry/${inquiryId}`)
  }

  return (
    <Wrapper onClick={() => handleClick(id)}>
      <ItemBox>
        <CategoryTag>{category_desc}</CategoryTag>
        <Title>{title}</Title>
      </ItemBox>
      <ItemBox>
        <DateText>{fDate(updated_at, 'yyyy.MM.dd')}</DateText>
        <StatusBox>
          <Round color={color} />
          <StatusText $color={color}>{status_desc}</StatusText>
        </StatusBox>
      </ItemBox>
    </Wrapper>
  )
}

export default InquiryListItem

const Wrapper = styled.div`
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: space-between;

  box-sizing: border-box;
  width: 100%;
  height: 89px;
  padding: 24px;

  border: 1px solid #c1c3c2;
  border-radius: 12px;

  @media ${theme.device.medium} {
    flex-direction: column;
    align-items: flex-start;
    height: 222px;
    padding: 16px;
  }
`

const ItemBox = styled.div`
  display: flex;
  gap: 24px;
  align-items: center;
  justify-content: center;

  @media ${theme.device.medium} {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }
`

const CategoryTag = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;
  width: 164px;
  height: 41px;
  padding: 16px 8px;

  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 25px;
  color: #313634;

  background-color: #eaebeb;
  border-radius: 60px;

  @media ${theme.device.medium} {
    width: 134px;
    height: 37px;
    padding: 12px 8px;

    font-size: 15px;
    line-height: 21px;
  }
`
const Title = styled.p`
  overflow: hidden;

  max-width: 518px;

  font-size: 18px;
  font-weight: 500;
  line-height: 25px;
  color: #313634;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media ${theme.device.medium} {
    max-width: 100%;
    font-size: 15px;
    line-height: 21px;
  }
`

const DateText = styled.span`
  font-size: 18px;
  font-weight: 500;
  line-height: 25px;
  color: #838685;

  @media ${theme.device.medium} {
    font-size: 15px;
    line-height: 21px;
  }
`

const StatusBox = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;

  width: 100%;

  @media ${theme.device.medium} {
    gap: 8px;
  }
`

const StatusText = styled.span<{ $color: string }>`
  font-family: NanumSquareNeo-Bold, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 25px;
  color: ${({ $color }) => $color};

  @media ${theme.device.medium} {
    font-size: 15px;
    line-height: 21px;
  }
`
